
const DEFAULT_BTNNAME = 'Subscribe';
const DEFAULT_SEPARATED = true;
const DEFAULT_BTNVALIDATIONERROR = Object.freeze({ backgroundColor: "#e74c3c" });
const DEFAULT_BTNVALIDATIONSUCCESS = Object.freeze({ backgroundColor: "#27ae60" });
const DEFAULT_RECAPTCHA = false;
const DEFAULT_VALIDATION = true;
const DEFAULT_SUCCESSERROR = false;

/**
 * Returns a default hover color for the button if this one is not precised
 * @param {string} hex - default hexa color
 * @param {number} lum - luminance that we want
 * @return {string} - a new hexa color
**/
const colorLuminance = (hex, lum = -0.2) => {
    // validate hex string
    hex = String(hex).replace(/[^0-9a-f]/gi, '');
    if (hex.length < 6) {
        hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    }
    lum = lum || 0;

    // convert to decimal and change luminosity
    var rgb = "#", c, i;
    for (i = 0; i < 3; i++) {
        c = parseInt(hex.substr(i*2,2), 16);
        c = Math.round(Math.min(Math.max(0, c + (c * lum)), 255)).toString(16);
        rgb += ("00"+c).substr(c.length);
    }

    return rgb;
};

/** Force an array to a single value */
function unlist(value) {
    return value instanceof Array ? value[0] : value;
}

/** Force a bool or default value */
function checkBool(value, default_) {
    value = unlist(value);
    if (value == null ||  value === undefined) {
        return default_;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    // Cast '', 'false' and '0' as False, everything else as true
    return value && value.toLowerCase() !== 'false' && value !== '0';
}

/** Force a string or default value */
function checkString(value, default_) {
    return unlist(value) || default_;
}

const HEXCOLOR_RE = /^[a-fA-F0-9]{6}$/;

/** Force a color or default value */
function checkColor(value, default_) {
    value = unlist(value) || default_;
    // It looks like a hexa code for a color, prepend the # prefix
    if (HEXCOLOR_RE.test(value)) {
        return `#${value}`;
    }
    return value;
}

/** Force a size in px */
function checkSize(value, default_) {
    if (!value && !default_) {
        return null;
    }
    return `${parseInt(unlist(value)) || default_ }px`;
}

/** Icon class for icon or btnicon */
class Icon {
    constructor(options) {
        if (options === undefined) {
            options = { active: false };
        } else if (typeof options === 'string') {
            options = { name: options, active: true };
        } else {
            options = { active: true, ...options };
        }
        this._options = options || {};
    }

    get active() {
        return checkBool(this._options.active, false);
    }

    get name() {
        return checkString(this._options.name, '');
    }

    get color() {
        return checkColor(this._options.color, '#9e9e9e');
    }
}

class Style {
    constructor({ color, backgroundColor, ...rest }) {
        Object.assign(this, rest);
        this.color = checkColor(color);
        this.backgroundColor = checkColor(backgroundColor);
    }
}

/** Icon class for icon or btnicon */
class StyledText {
    constructor(_options) {

        if (typeof _options === 'string') {
            _options =  { text: _options };
        }

        const { text, active, ...options } = _options || {};
        this._active = checkBool(active, Boolean(_options));
        this._text = text;
        this._options = options;
    }

    get active() {
        return this._active;
    }

    get style() {
        return new Style(this._options);
    }

    get text() {
        return checkString(this._text, '');
    }
}

/** the model for the configuration of model */
export default class ModuleConfiguration {
    constructor(options) {
        this._options = Object.freeze(options || {});
    }

    /** the text of the send button  */
    get btnName() {
        return checkString(this._options.btnname, DEFAULT_BTNNAME);
    }

    /** when true, the button is separated from the button */
    get separated() {
        return checkBool(this._options.separated, DEFAULT_SEPARATED);
    }

    /** when true the button display when success or error send */
    get validation() {
        return checkBool(this._options.validation, DEFAULT_VALIDATION);
    }

    /** when true change the color of the input when error or success append */
    get successError() {
        return checkBool(this._options.successerror, DEFAULT_SUCCESSERROR);
    }

    /** The icon associated with the inputs */
    get icon() {
        return new Icon(unlist(this._options.icon));
    }

    /** The checkbox position in Boolean field case */
    get checkboxPosition() {
        return this._options.checkboxPosition || "left";
    }

    /** The list of fields in order. It always contains `email` */
    get fields() {
        let fields = this._options.fields || [];
        // fields is a string, maybe a comma separated list of fields.
        if (typeof fields === 'string') {
            fields = fields.split(',');
        }
        if (!(fields instanceof Array)) {
            fields = [];
        }
        // ensure that email is always present
        if (!fields.includes('email')) {
            fields.unshift('email');
        }
        return fields;
    }

    /** The icon associated with the button */
    get btnIcon() {
        return new Icon(unlist(this._options.btnicon));
    }

    /** The placeholders for each field */
    get placeholders() {
        const placeholders = this._options.placeholders || {};
        // Legacy: the `placeholder` is the value of the email placeholder
        if (this._options.placeholder && !placeholders.email) {
            return { ...placeholders, email: this._options.placeholder };
        }
        return  placeholders;
    }

    /** Get fields types,if there's no type, we will set String by default */
    get types() {
        return this._options.types || {};
    }

    get choices() {
        const choices = this._options.choices || {};
        let formattedChoices = {};
        // Format choices object to get an object of Arrays who worked with semantic Select input
        // Example
        // { gender: [{ key: 'male', value: 'male', text: 'Male' }, {key: 'female', value: 'female', text: 'Female'}]}
        Object.entries(choices).forEach(([field, choices]) => {
            formattedChoices[field] = Object.entries(choices).map(([value, text]) => {
                return {key: value, value: text, text: text};
            });
        });
        return formattedChoices;
    }

    /** The size in px of the form */
    get wrapperWidth() {
        return checkSize(this._options.wrapperWidth);
    }

    /** The default style of the Send button */
    get btnDefault() {
        // Get base value
        const btnDefault = unlist(this._options.btndefault) || {};

        // Generate overrides from related parameters
        const overrides = {};

        if(btnDefault.borderRadius){
            overrides.borderRadius = checkSize(btnDefault.borderRadius);
        }

        if (this.fields.length > 1) {
            overrides.width = "100%";
            overrides.maxWidth = this.wrapperWidth;
        }

        if(!this.separated) {
            overrides.borderTopLeftRadius = 0;
            overrides.borderBottomLeftRadius = 0;
        } else if (this.fields.length === 1) {
            overrides.marginLeft = '10px';
        }

        return new Style({ ...btnDefault, ...overrides });
    }

    /** The style of the Send button when the screen is small. It's merged with btnDefault */
    get btnDefaultSmallScreen() {
        const overrides = {
            marginLeft: 0,
            margin: 0,
            width: "100%",
            maxWidth: this.wrapperWidth,
        };

        return new Style({
            ...this.btnDefault,
            ...overrides,
        });
    }

    /** The style of the Send button when hovered and the screen is small. It's merged with btnDefault */
    get btnDefaultSmallScreenHover() {
        const overrides = {
            marginLeft: 0,
            margin: 0,
            width: "100%",
            maxWidth: this.wrapperWidth,
        };

        //add a default hover color if it is not precised
        if (this.btnDefault.backgroundColor) {
            overrides.backgroundColor = colorLuminance(this.btnDefault.backgroundColor);
        }

        return new Style({
            ...this.btnDefault,
            ...overrides,
            ...unlist(this._options.btnhover) || {},
        });
    }

    /** The style of the Send button when hovered. It's merged with btnDefault */
    get btnHover() {
        const overrides = {};

        //add a default hover color if it is not precised
        if (this.btnDefault.backgroundColor) {
            overrides.backgroundColor = colorLuminance(this.btnDefault.backgroundColor);
        }

        return new Style({
            ...this.btnDefault,
            ...overrides, // overrides default, but do not override btnHover
            ...unlist(this._options.btnhover) || {},
        });
    }

    /** The style of the Send button when send failed. It's merged with btnDefault */
    get btnValidationError() {
        return new Style({
            ...this.btnDefault,
            ...unlist(this._options.btnvalidationerror) || DEFAULT_BTNVALIDATIONERROR,
        });
    }

    /** The style of the Send button when send succeeded. It's merged with btnDefault */
    get btnValidationSuccess() {
        return new Style({
            ...this.btnDefault,
            ...unlist(this._options.btnvalidationsuccess) || DEFAULT_BTNVALIDATIONSUCCESS,
        });
    }

    /** The content and style of the error message */
    get errorMessage() {
        return new StyledText(this._options.errormessage);
    }

    /** The content and style of the success message */
    get successMessage() {
        return new StyledText(this._options.successmessage);
    }

    /** The content and style of the invalid form message */
    get inputMessage() {
        return new StyledText(this._options.inputmessage);
    }

    get recaptcha() {
        return checkBool(this._options.recaptcha, DEFAULT_RECAPTCHA);
    }
}
